import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * Cozy Bundle & Save: our own quantity breaks box (same look as the Pumper box on the regular Unbasic tee).
 * The customer picks 1, 3 or 6 T-shirts and a size/fit/colour for each; the theme's Add to cart button then adds
 * them all. The discount itself is a Shopify automatic discount (minimum quantity), not this script.
 */
// Pre-order on WhatsApp (owner, 2 Nov 2026, same as on Calin): the message carries the customer's whole order.
function whatsappLink(base, message) {
  const a = document.createElement('a');
  a.href = `${base}${base.includes('?') ? '&' : '?'}text=${encodeURIComponent(message)}`;
  a.target = '_blank';
  a.rel = 'noopener';
  a.className = 'czb-wa-btn';
  a.innerHTML =
    '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.4.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.1.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3Z"/></svg>' +
    '<span>Pre-order on WhatsApp</span>';
  return a;
}

class CozyBundle extends HTMLElement {
  #offer = null;
  #product = null;
  #selected = 0;
  #picks = [];
  #moneyFormat = '${{amount}}';
  #onAdd = null;

  connectedCallback() {
    if (this.#offer) return; // theme morphs can re-attach the element
    try {
      this.#offer = JSON.parse(this.querySelector('script[data-cozy-bundle-offer]').textContent);
      this.#product = JSON.parse(this.querySelector('script[data-cozy-bundle-product]').textContent);
    } catch {
      return;
    }
    if (!this.#offer?.tiers?.length) return;
    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;
    const def = parseInt(this.#offer.default_selected, 10);
    this.#selected = def >= 0 && def < this.#offer.tiers.length ? def : 0;

    const first = this.#urlVariant() || this.#firstAvailable();
    this.#picks = this.#offer.tiers.map((t) => Array.from({ length: t.qty }, () => first.id));
    this.#render();
    this.classList.add('is-ready');

    this.#onAdd = (e) => this.#handleAdd(e);
    document.addEventListener('click', this.#onAdd, true);
    document.addEventListener('submit', this.#onAdd, true);
  }

  disconnectedCallback() {
    if (!this.#onAdd) return;
    document.removeEventListener('click', this.#onAdd, true);
    document.removeEventListener('submit', this.#onAdd, true);
    this.#onAdd = null;
    this.#offer = null;
  }

  /* ---------- helpers ---------- */

  #urlVariant() {
    const id = new URL(window.location.href).searchParams.get('variant');
    return this.#product.variants.find((v) => String(v.id) === id && v.available) || null;
  }

  #firstAvailable() {
    return this.#product.variants.find((v) => v.available) || this.#product.variants[0];
  }

  #variant(id) {
    return this.#product.variants.find((v) => v.id === id) || this.#product.variants[0];
  }

  #money(cents) {
    return formatMoney(cents, this.#moneyFormat, window.Shopify?.currency?.active || '');
  }

  // Shopify takes the % off the whole line, so work it out the same way and show it per T-shirt.
  #prices(index) {
    const tier = this.#offer.tiers[index];
    const lines = this.#picks[index].map((id) => this.#variant(id));
    const full = lines.reduce((sum, v) => sum + v.price, 0);
    const total = full - Math.round((full * tier.percent) / 100);
    const v = lines[0];
    const compare = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : null;
    return { each: Math.round(total / tier.qty), total, compare };
  }

  /* ---------- drawing ---------- */

  #render() {
    const list = document.createElement('div');
    list.className = 'czb-tiers';
    list.setAttribute('role', 'radiogroup');
    this.#offer.tiers.forEach((tier, i) => list.appendChild(this.#renderTier(tier, i)));

    const total = document.createElement('div');
    total.className = 'czb-total';
    total.innerHTML = '<span></span><b></b>';
    total.firstChild.textContent = this.#offer.total_text || 'Total:';
    total.lastChild.textContent = this.#money(this.#prices(this.#selected).total);

    this.replaceChildren(...this.querySelectorAll('script'), list, total);
  }

  #renderTier(tier, index) {
    const selected = index === this.#selected;
    const card = document.createElement('div');
    card.className = 'czb-tier' + (selected ? ' is-selected' : '');

    if (tier.label) {
      const ribbon = document.createElement('span');
      ribbon.className = 'czb-ribbon';
      ribbon.textContent = tier.label;
      card.appendChild(ribbon);
    }

    const head = document.createElement('label');
    head.className = 'czb-head';
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.className = 'czb-radio';
    radio.name = `czb-${this.#product.id}`;
    radio.checked = selected;
    radio.addEventListener('change', () => {
      this.#selected = index;
      this.#render();
    });
    const title = document.createElement('span');
    title.className = 'czb-title';
    title.textContent = tier.title;
    head.append(radio, title);

    if (tier.subtitle) {
      const pill = document.createElement('span');
      pill.className = 'czb-pill';
      pill.textContent = tier.subtitle;
      head.appendChild(pill);
    }

    const { each, compare } = this.#prices(index);
    const price = document.createElement('span');
    price.className = 'czb-price';
    const now = document.createElement('span');
    now.className = 'czb-now';
    now.textContent = this.#money(each);
    const eachText = document.createElement('span');
    eachText.className = 'czb-each';
    eachText.textContent = ` ${this.#offer.each_text || '/each'}`;
    now.appendChild(eachText);
    price.appendChild(now);
    if (compare) {
      const was = document.createElement('s');
      was.className = 'czb-was';
      was.textContent = this.#money(compare);
      price.appendChild(was);
    }
    head.appendChild(price);
    card.appendChild(head);

    if (selected) card.appendChild(this.#renderPickers(index));
    return card;
  }

  #renderPickers(index) {
    const options = this.#product.options;
    const grid = document.createElement('div');
    grid.className = 'czb-pickers';
    grid.style.setProperty('--czb-cols', String(options.length));

    grid.appendChild(document.createElement('span'));
    for (const o of options) {
      const label = document.createElement('span');
      label.className = 'czb-label';
      label.textContent = o.name;
      grid.appendChild(label);
    }

    // An option value nobody can buy in any combination (e.g. the Relaxed fit) is the one to box in red;
    // otherwise the size is (owner, 2 Nov: "a red rectangle around the size or fit that's not available")
    const goneEverywhere = (oi, value) => !this.#product.variants.some((v) => v.options[oi] === value && v.available);
    const sizeOi = Math.max(0, options.findIndex((o) => /size/i.test(o.name)));
    const culprits = (v) => {
      const gone = options.map((_, oi) => oi).filter((oi) => goneEverywhere(oi, v.options[oi]));
      return gone.length ? gone : [sizeOi];
    };
    let soldOut = null;
    this.#picks[index].forEach((id, item) => {
      const current = this.#variant(id);
      const red = current.available ? [] : culprits(current);
      if (!current.available && !soldOut) soldOut = { v: current, red };
      const num = document.createElement('span');
      num.className = 'czb-num';
      num.textContent = `${item + 1}.`;
      grid.appendChild(num);

      options.forEach((option, oi) => {
        const select = document.createElement('select');
        select.className = 'czb-select' + (red.includes(oi) ? ' is-soldout' : '');
        select.setAttribute('aria-label', `${option.name} ${item + 1}`);
        for (const value of option.values) {
          const opt = document.createElement('option');
          opt.value = value;
          const wanted = [...current.options];
          wanted[oi] = value;
          const v = this.#product.variants.find((x) => x.options.every((val, k) => val === wanted[k]));
          // Sizes are only marked when the size is the problem (not when the fit itself is gone, as with Relaxed)
          const otherGone = options.some((_, k) => k !== oi && goneEverywhere(k, current.options[k]));
          const gone = goneEverywhere(oi, value) || (oi === sizeOi && !otherGone && v && !v.available);
          // Owner, 3 Nov: a value that's coming back (offer data "restock", e.g. Relaxed) says COMING SOON, not SOLD OUT
          const soon = goneEverywhere(oi, value) && (this.#offer.restock || {})[value];
          opt.textContent = soon ? `${value} - COMING SOON` : gone ? `${value} - SOLD OUT` : value;
          opt.selected = current.options[oi] === value;
          select.appendChild(opt);
        }
        select.addEventListener('change', () => {
          const wanted = [...current.options];
          wanted[oi] = select.value;
          const exact = this.#product.variants.find((v) => v.options.every((val, k) => val === wanted[k]));
          const match =
            exact ||
            this.#product.variants.find((v) => v.options[oi] === select.value && v.available) ||
            this.#product.variants.find((v) => v.options[oi] === select.value);
          if (match) this.#picks[index][item] = match.id;
          this.#render();
        });
        grid.appendChild(select);
      });
    });

    if (soldOut) {
      const { v, red } = soldOut;
      const restock = this.#offer.restock || {};
      const back = red.map((oi) => restock[v.options[oi]]).find(Boolean);
      const ci = options.findIndex((o) => /colou?r/i.test(o.name));
      const note = document.createElement('div');
      note.className = 'czb-oos';
      note.setAttribute('role', 'alert');
      const msg = document.createElement('span');
      msg.textContent = back
        ? `${back} Pre-order it on WhatsApp.`
        : `Size ${v.options[sizeOi]} is SOLD OUT${ci >= 0 ? ` in ${v.options[ci]}` : ''}. Pre-order it on WhatsApp.`;
      note.appendChild(msg);
      if (this.#offer.whatsapp) note.appendChild(whatsappLink(this.#offer.whatsapp, this.#orderMessage(index)));
      grid.appendChild(note);
    }
    return grid;
  }

  // "Unbasic T-Shirt 2.0 L / Relaxed / Black x2 (SOLD OUT)" lines, same choices grouped, with the deal picked
  #orderMessage(index) {
    const tier = this.#offer.tiers[index];
    const counts = new Map();
    for (const id of this.#picks[index]) {
      const v = this.#variant(id);
      const soon = v.options.some((o) => (this.#offer.restock || {})[o]);
      const key = `${this.#product.title || this.dataset.productTitle || ''} ${v.options.join(' / ')}${v.available ? '' : soon ? ' (COMING SOON)' : ' (SOLD OUT)'}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const lines = ['Hello! I would like to pre-order!'];
    if (tier.qty > 1) lines.push(`Deal: ${tier.title}${tier.subtitle ? ` (${tier.subtitle})` : ''}`);
    lines.push(...[...counts].map(([line, n]) => `- ${line} x${n}`));
    return lines.join('\n');
  }

  /* ---------- add to cart ---------- */

  #ourForm(event) {
    const form = document
      .querySelector(`form[data-type="add-to-cart-form"] input[name="product-id"][value="${this.#product.id}"]`)
      ?.closest('form');
    if (!form) return null;
    if (event.type === 'submit') return event.target === form ? form : null;
    const button = event.target.closest?.('button[name="add"], .sticky-add-to-cart__button');
    if (!button) return null;
    if (form.contains(button) || button.closest('sticky-add-to-cart')) return form;
    return null;
  }

  async #handleAdd(event) {
    const form = this.#ourForm(event);
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.dataset.busy) return;

    const picks = this.#picks[this.#selected];
    if (picks.some((id) => !this.#variant(id).available)) {
      this.querySelector('.czb-oos')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }

    const qty = new Map();
    for (const id of picks) qty.set(id, (qty.get(id) || 0) + 1);

    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sections.add(el.dataset.sectionId);
    });

    this.dataset.busy = 'true';
    const buttons = document.querySelectorAll('button[name="add"], .sticky-add-to-cart__button');
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      const body = JSON.stringify({
        items: Array.from(qty, ([id, quantity]) => ({ id, quantity })),
        sections: Array.from(sections).join(','),
        sections_url: window.location.pathname,
      });
      const res = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }));
      const added = await res.json();
      if (!res.ok || added.status) throw new Error(added.description || added.message || 'Could not add to cart');

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      document.dispatchEvent(
        new CartAddEvent(cart, 'cozy-bundle', {
          source: 'cozy-bundle',
          itemCount: cart.item_count,
          productId: String(this.#product.id),
          variantId: String(picks[0]),
          sections: added.sections,
        })
      );
    } catch (err) {
      console.error('[cozy-bundle]', err);
      try {
        document.dispatchEvent(new CartErrorEvent('cozy-bundle', err?.message || 'Could not add to cart'));
      } catch {}
    } finally {
      delete this.dataset.busy;
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }
}

customElements.get('cozy-bundle') || customElements.define('cozy-bundle', CozyBundle);
